import React, { useState } from 'react'
import moment from 'moment-timezone'
import DatetimeRenderer from './DatetimeRenderer'
import './HistoryList.css'

const formatJson = (value) => {
    if (value === null || value === undefined) return '--'
    if (typeof value === 'string') return value

    try {
        return JSON.stringify(value, null, 2)
    } catch {
        return String(value)
    }
}

const HIDDEN_CHANGE_FIELDS = new Set(['date_modified'])
const SNAPSHOT_FIELD_ALIASES = {
    modified_by: 'modified_by_id',
    created_by: 'created_by_id',
    assigned_by: 'assigned_by_id',
}

// Values are shown as written for text, and NULL where there was no value at
// all -- an empty cell would read as "we don't know" rather than "it was unset".
const formatChangeValue = (value) => {
    if (value === null || value === undefined || value === '') return 'NULL'
    if (typeof value === 'string') return value

    try {
        return JSON.stringify(value)
    } catch {
        return String(value)
    }
}

// Humanize a field/model name: drop a trailing _id, split on _, Title Case.
const humanizeName = (name) => String(name || '')
    .replace(/_id$/, '')
    .split('_')
    .filter(Boolean)
    .map((part) => part.charAt(0).toUpperCase() + part.slice(1))
    .join(' ')

const joinReadable = (labels) => {
    if (labels.length === 0) return ''
    if (labels.length === 1) return labels[0]
    if (labels.length === 2) return `${labels[0]} and ${labels[1]}`
    return `${labels.slice(0, -1).join(', ')}, and ${labels[labels.length - 1]}`
}

// When the backend has no comment for a row, summarize the event/changes so the
// timeline still shows a human-readable description of what happened.
const generateSummary = (item, changedFieldNames) => {
    const modelLabel = humanizeName(item.modelName) || 'record'
    if (item.isNew) return `New ${modelLabel.toLowerCase()} created.`
    if (item.isDeleted) return `${modelLabel} deleted.`
    if (!changedFieldNames.length) return 'Updated with no tracked field changes.'
    return `Updated ${joinReadable(changedFieldNames.map(humanizeName))}.`
}

const normalizeForComparison = (value) => {
    if (value === null || value === undefined || value === '') return null
    if (Array.isArray(value)) return value.map(normalizeForComparison)
    if (value && typeof value === 'object') {
        return Object.keys(value)
            .sort()
            .reduce((acc, key) => {
                acc[key] = normalizeForComparison(value[key])
                return acc
            }, {})
    }
    return value
}

const areValuesEqual = (left, right) => (
    JSON.stringify(normalizeForComparison(left)) === JSON.stringify(normalizeForComparison(right))
)

const getSnapshotFieldName = (fieldName) => SNAPSHOT_FIELD_ALIASES[fieldName] || fieldName

// A snapshot value can be a whole serialised object. Past this, one long value
// pushes the From/To pair off the row and buries every other change on it.
const VALUE_TRUNCATE_AT = 48

/**
 * A change value, shortened until it is asked for in full. Each one holds its
 * own state, so opening a long "To" leaves the "From" beside it untouched.
 */
function ChangeValue({ value }) {
    const [expanded, setExpanded] = useState(false)

    if (value.length <= VALUE_TRUNCATE_AT) {
        return <span className="vgm-audit-change-value">{value}</span>
    }

    return (
        <span className="vgm-audit-change-value">
            {expanded ? value : value.slice(0, VALUE_TRUNCATE_AT)}
            <button
                type="button"
                className="vgm-audit-value-toggle"
                aria-expanded={expanded}
                title={expanded ? 'Show less' : 'Show the full value'}
                onClick={() => setExpanded((current) => !current)}
            >
                {expanded ? 'less' : '…'}
            </button>
        </span>
    )
}

/**
 * The activity table for a set of history items. Shared by the single-record
 * audit trail (BQHistoryList) and any caller that already has rows fetched by
 * some other means (e.g. a per-user history view).
 *
 * `nullActorLabel` lets a caller rename the fallback shown for a null actor
 * in the Who column -- the record-history modals keep "System", but the
 * Investigate tab's own picker says "No Actor" for the same selection
 * (`InvestigateTab.js`), and the results table below it should match.
 */
function HistoryTable({ items, alwaysShowObject = false, nullActorLabel = 'System' }) {
    const [visibleDetails, setVisibleDetails] = useState({})

    const getItemKey = (item, index) => (
        `${item.appLabel}-${item.modelName}-${item.objectId}-${item.eventTimestamp || index}-${index}`
    )
    const toggleDetails = (key) => {
        setVisibleDetails((prev) => ({ ...prev, [key]: !prev[key] }))
    }

    // Callers pass a single object today, so the modal title already names the
    // subject and repeating it on every row is noise. Kept for the case where a
    // caller asks for several objects at once, where the row is ambiguous without it.
    const spansSeveralObjects = new Set(items.map((item) => `${item.modelName}#${item.objectId}`)).size > 1
    const showObjectColumn = alwaysShowObject || spansSeveralObjects
    const columnCount = showObjectColumn ? 6 : 5

    return (
        <div className="vgm-audit-tablewrap">
            <table className="vgm-audit-table">
                <thead>
                    <tr>
                        {/* Only earns a column when the log actually covers several
                            records -- otherwise the modal title already names the one.
                            Leads the row when it is there: it says which record the
                            rest of the row is about. */}
                        {showObjectColumn && <th className="vgm-audit-th">Object</th>}
                        <th className="vgm-audit-th">Activity</th>
                        <th className="vgm-audit-th">Who</th>
                        <th className="vgm-audit-th">When</th>
                        <th className="vgm-audit-th">Change</th>
                        <th className="vgm-audit-th vgm-audit-snapshot-col">
                            <span className="vgm-audit-sr-only">Snapshot</span>
                        </th>
                    </tr>
                </thead>
                <tbody>
                    {items.map((item, index) => {
                            const itemKey = getItemKey(item, index)
                            const isEvent = item.isNew || item.isDeleted
                            const visibleChanges = !isEvent && item.dirtyFields && typeof item.dirtyFields === 'object'
                                ? Object.entries(item.dirtyFields).filter(([fieldName, oldValue]) => {
                                    const snapshotFieldName = getSnapshotFieldName(fieldName)
                                    return (
                                        !HIDDEN_CHANGE_FIELDS.has(fieldName)
                                        && !areValuesEqual(oldValue, item.snapshot?.[snapshotFieldName])
                                    )
                                })
                                : []
                            const isDetailsVisible = visibleDetails[itemKey]
                            // Use the backend comment when present; otherwise generate a
                            // summary of the event/changes so every row has a description.
                            const description = item.comment
                                || generateSummary(item, visibleChanges.map(([fieldName]) => fieldName))
                            // The dot's colour is the only ornament here, and it carries
                            // information: created, deleted, or a plain update.
                            const kind = item.isNew ? 'created' : (item.isDeleted ? 'deleted' : 'updated')
                            return (
                                <React.Fragment key={itemKey}>
                                    <tr className={`vgm-audit-row${isDetailsVisible ? ' is-open' : ''}`}>
                                        {showObjectColumn && (
                                            <td className="vgm-audit-cell vgm-audit-related-cell">
                                                <span className="vgm-audit-object">
                                                    {humanizeName(item.modelName) || 'Record'}
                                                </span>
                                                <span className="vgm-audit-object-id">#{item.objectId}</span>
                                            </td>
                                        )}
                                        <td className="vgm-audit-cell vgm-audit-activity-cell">
                                            <div className="vgm-audit-activity">
                                                <span className={`vgm-audit-marker is-${kind}`} aria-hidden="true" />
                                                <span>{description}</span>
                                            </div>
                                        </td>
                                        <td className="vgm-audit-cell">
                                            <span className={`vgm-audit-actor${item.actorName ? '' : ' is-system'}`}>
                                                {item.actorName || nullActorLabel}
                                            </span>
                                        </td>
                                        <td className="vgm-audit-cell vgm-audit-when-cell">
                                            {/* "4 days ago" answers the question a bare stamp
                                                only lets you work out; the exact moment, in every
                                                timezone that matters, is a hover away. */}
                                            <span className="vgm-audit-when">
                                                {item.eventTimestamp
                                                    ? <DatetimeRenderer datetime={item.eventTimestamp} label={moment(item.eventTimestamp).fromNow()} placement="top" />
                                                    : '--'}
                                            </span>
                                        </td>
                                        <td className="vgm-audit-cell vgm-audit-change-cell">
                                            {visibleChanges.length === 0
                                                ? <span className="vgm-audit-nochange">—</span>
                                                : (
                                                    <div className="vgm-audit-changes">
                                                        {visibleChanges.map(([fieldName, oldValue]) => {
                                                            const snapshotFieldName = getSnapshotFieldName(fieldName)
                                                            return (
                                                                <div key={fieldName} className="vgm-audit-change">
                                                                    <span className="vgm-audit-change-field">{humanizeName(fieldName)}</span>
                                                                    <span className="vgm-audit-change-label">From:</span>
                                                                    <ChangeValue value={formatChangeValue(oldValue)} />
                                                                    <span className="vgm-audit-change-label is-to">To:</span>
                                                                    <ChangeValue value={formatChangeValue(item.snapshot?.[snapshotFieldName])} />
                                                                </div>
                                                            )
                                                        })}
                                                    </div>
                                                )}
                                        </td>
                                        <td className="vgm-audit-cell vgm-audit-snapshot-col">
                                            <button
                                                type="button"
                                                className="vgm-audit-toggle"
                                                aria-expanded={!!isDetailsVisible}
                                                aria-controls={`${itemKey}-snapshot`}
                                                onClick={() => toggleDetails(itemKey)}
                                            >
                                                <i className={`fa fa-chevron-right vgm-audit-toggle-icon${isDetailsVisible ? ' is-open' : ''}`} aria-hidden="true" />
                                                Snapshot
                                            </button>
                                        </td>
                                    </tr>
                                    {isDetailsVisible && (
                                        <tr className="vgm-audit-snapshot-row">
                                            <td className="vgm-audit-snapshot-body" colSpan={columnCount}>
                                                <pre id={`${itemKey}-snapshot`} className="vgm-audit-json">{formatJson(item.snapshot)}</pre>
                                            </td>
                                        </tr>
                                    )}
                                </React.Fragment>
                            )
                    })}
                </tbody>
            </table>
        </div>
    )
}

export default HistoryTable
